import { RefObject, useEffect, useState } from 'react';
import {
  isPdfContent,
  renderedPdfFirstPage,
  renderPdfFirstPage,
} from 'helpers/pdf-first-page';
import { AlbumContentSerialized } from 'networking/types/album-detail';

// The first page of a PDF memory as an image URL, for its thumbnail. It's only
// drawn once `ref`'s element comes near the viewport, so a long grid fetches
// just the PDFs on screen. null until then, and for anything that isn't a
// readable PDF.
export const usePdfFirstPage = (
  content: AlbumContentSerialized,
  ref: RefObject<Element | null>,
) => {
  const url = isPdfContent(content) ? content.mediaUrl : null;
  const size = content.fileSize;
  const key = String(content.id);
  const [src, setSrc] = useState(() =>
    url ? renderedPdfFirstPage(key) : null,
  );

  useEffect(() => {
    const ready = url ? renderedPdfFirstPage(key) : null;
    setSrc(ready);
    const element = ref.current;
    if (
      !url ||
      ready ||
      !element ||
      typeof IntersectionObserver === 'undefined'
    )
      return undefined;

    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        renderPdfFirstPage(url, key, size).then(
          (page) => {
            if (active) setSrc(page);
          },
          // Unreadable PDFs keep their file graphic.
          () => {},
        );
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [url, key, size]);

  return src;
};
